import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  CASH_FLOW_TABS,
  cashFlowInitialValues,
} from 'components/organisms/forms/cash-flow/CashFlowForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useCustomParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetCost,
  usePutCost,
  usePutCostFile,
} from 'services/cash-flow/cost';
import { ICashFlowForm } from 'services/cash-flow/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditCost = () => {
  const labelId = useCustomParamId('id', '/label');
  const costId = useCustomParamId('cashFlowId', `/label/${labelId}/cost`);

  const { pathname } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const mutateOptions = useMutateOptions<ICashFlowForm>();

  const cost = useGetCost(costId);

  const updateCost = usePutCost();

  const addFile = usePutCostFile();

  const onGoToDetail = () =>
    navigate(`/label/${labelId}/cost/${costId}/${getLastPathname(pathname)}`);

  const addFileToCost = async (file: File) => {
    await addFile.mutateAsync(
      { file, id: costId },
      {
        onSuccess: () => onGoToDetail(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onUpdateCost = async (
    values: ICashFlowForm,
    formikHelpers: FormikHelpers<ICashFlowForm>
  ) => {
    await updateCost.mutateAsync(
      {
        ...middleware.cost.formToQuery(values),
        settlementCostId: costId,
      },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: () =>
          values.file ? addFileToCost(values.file) : onGoToDetail(),
      }
    );
  };

  return (
    <Form.CashFlow
      initialValues={
        cost.data
          ? middleware.cost.queryToForm(cost.data)
          : cashFlowInitialValues
      }
      onSubmit={onUpdateCost}
      disableTabs={[CASH_FLOW_TABS.repayments]}
      status={{ isLoading: cost.isLoading }}
      customBackItem={
        <Button onlyIcon onClick={onGoToDetail}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.edit')} />
        </Button>
      }
    />
  );
};

export default EditCost;
